import React, { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { useT, Namespace } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";
import { cn } from "@racoon/ui";
import { realOrgId } from "~/features/map-view/model/owner-org";
import { ShiftPlayback } from "~/pages/dashboard/gps-tracking/components/shift-playback";

interface GpsTracksTabProps {
  route: ConnectProto.Route | undefined;
  job: ConnectProto.Job | undefined;
  /** Overrides for the map wrapper's fixed height when the parent sizes it. */
  className?: string;
}

/**
 * The truck's track over the route window, played back with the same map, player
 * and segments table as "Контроль движения".
 */
export const GpsTracksTab: React.FC<GpsTracksTabProps> = ({ route, job, className }) => {
  const t = useT();
  // A job loaded on its own (a map's photo pin) may carry no shift; its route does.
  const shift = job?.driverOnShift ?? route?.driverOnShift;
  const startTime = fromTimestamp(route?.factStartAt) ?? fromTimestamp(route?.planStartAt);
  const endTime = fromTimestamp(route?.factEndAt) ?? fromTimestamp(route?.planEndAt);
  // The truck's org, not the route's: the RPCs match the truck's owner exactly
  // and the two tiers disagree on most routes.
  const ownerOrgId =
    realOrgId(shift?.truck?.ownerOrganizationId) ?? realOrgId(route?.ownerOrganizationId);

  const truck = useMemo(
    () =>
      shift?.truck && ownerOrgId != null
        ? { ...shift.truck, ownerOrganizationId: ownerOrgId }
        : undefined,
    [shift?.truck, ownerOrgId],
  );

  // One route with this job only: the timeline segments, the table and the
  // pickup markers are all built from `route.jobs`.
  const routes = useMemo(() => (route && job ? [{ ...route, jobs: [job] }] : []), [route, job]);

  const startMs = startTime?.getTime();
  const endMs = endTime?.getTime();
  const trackWindow = useMemo(
    () =>
      startMs !== undefined && endMs !== undefined
        ? { from: new Date(startMs), to: new Date(endMs) }
        : null,
    [startMs, endMs],
  );

  if (!shift?.truck || !trackWindow) {
    return (
      <div className="flex items-center justify-center h-[400px] text-text-secondary">
        <span>{t(Namespace.GpsTracking, "notEnoughDataGPS")}</span>
      </div>
    );
  }

  // Distinct from "no GPS data": the track query can't run without an org, so
  // the map would otherwise look like a route nobody drove.
  if (!truck) {
    return (
      <div className="flex items-center justify-center h-[400px] text-text-secondary">
        <span>{t(Namespace.GpsTracking, "noOrganizationGPS")}</span>
      </div>
    );
  }

  return (
    <div className={cn("w-full h-[100vh] max-h-[525px] mt-4 relative", className)}>
      <ShiftPlayback
        shift={shift}
        truck={truck}
        routes={routes}
        window={trackWindow}
        photosOpenReport={false}
      />
    </div>
  );
};
